import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type Dialog from "cc-frontend/services/dialog"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import { max } from "lodash-es"
import { tracked } from "tracked-built-ins"
import { localCopy } from "tracked-toolbox"
import styles from "./edit-annotation-body.module.css"

/**
 * Don't confuse with date annotation type. It looks very similar,
 * but this is used to dictate behavior on the select function of the power calendar
 */
export type DateIs = "SINGLE_DATE" | "FIXED_START_RANGE" | "FLEXIBLE_RANGE"

export interface Args {
  // submitAction: Function
  allowRangeToggle: boolean
  allowNoClassToggle?: boolean
  dateIs: DateIs
  datesAreEditable: boolean
  description: string | null
  classIsOff?: boolean
  endDate?: string | null
  minimumStartDate?: string
  startDate?: string | null
  submitButtonText: string
  title: string | null
  closeModal?: Function
  submitAction: (submitAction: SubmitAction) => any
}

export interface SubmitAction {
  title: string
  description: string
  startDate: string
  endDate: string | null
  isOff?: boolean
  isRange?: boolean
}

export default class EditAnnotationBodyComponent extends Component<Args> {
  @service declare dialog: Dialog
  @service declare courseCalendarEditor: CourseCalendarEditor
  @service planbookEditor!: PlanbookEditor

  readonly styles = styles

  @tracked center = null

  @localCopy("args.title") title
  @localCopy("args.description") description
  @localCopy("args.classIsOff", false) classIsOff
  @localCopy("args.startDate") startDateString
  @localCopy("args.endDate") endDateString
  // If editing a date annotation that is a range this will be passed in as true
  // When creating a date annotation, we assume a single date (false)
  // @localCopy("args.isRange", false) isRange
  @tracked isRange
  @localCopy("args.dateIs") declare dateIs: DateIs

  constructor(owner: unknown, args: Args) {
    super(owner, args)

    this.isRange =
      this.args.startDate && this.args.endDate && this.args.startDate !== this.args.endDate
  }

  get minimumStartDateAsDate() {
    return this.args.minimumStartDate ? dateFns.parse(this.args.minimumStartDate) : null
  }

  get startDateAsDate() {
    return this.startDateString ? dateFns.parse(this.startDateString) : null
  }

  get endDateAsDate() {
    return this.endDateString ? dateFns.parse(this.endDateString) : null
  }

  get showCalendar() {
    if (this.args.allowRangeToggle) {
      return this.isRange && this.args.datesAreEditable
    } else {
      return this.args.datesAreEditable
    }
  }

  get canSubmit() {
    return this.startDateString !== null
  }

  @action updateTitle(title) {
    this.title = title
  }

  @action updateDescription(description) {
    this.description = description
  }

  @action toggleRange() {
    this.isRange = !this.isRange
    this.dateIs = this.isRange ? "FIXED_START_RANGE" : "SINGLE_DATE"
  }

  @action toggleClassOff() {
    this.classIsOff = !this.classIsOff
  }
  @action setCenter(date, powerCalendar) {
    this.center = date.date
  }

  @action onSelect(params) {
    let start = params.date.start ? dateFns.format(params.date.start, "YYYY-MM-DD") : null
    let end = params.date.end ? dateFns.format(params.date.end, "YYYY-MM-DD") : null

    if (this.dateIs === "SINGLE_DATE") {
      this.startDateString = start
      this.endDateString = start
    } else if (this.dateIs === "FIXED_START_RANGE") {
      let newEnd = max([start, end])
      this.endDateString = newEnd < this.startDateString ? this.startDateString : newEnd
    } else if (this.dateIs === "FLEXIBLE_RANGE") {
      this.startDateString = start
      this.endDateString = end
    }
  }

  @action
  submit() {
    this.args.submitAction({
      title: this.title,
      description: this.description,
      startDate: this.startDateString,
      endDate: this.endDateString,
      isOff: this.classIsOff,
      isRange: this.isRange,
    })
    if (this.args.closeModal) {
      this.args.closeModal()
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Modals::EditAnnotationBody": typeof EditAnnotationBodyComponent
  }
}
